import { CheckCircle2, ArrowRight } from "lucide-react";
import { LazyVideo } from "./LazyVideo";
import { LaptopVideo } from "./LaptopVideo";
import pharmacyCbsAsset from "@/assets/pharmacy-valuation-imac.png.asset.json";
import campaignVideoAsset from "@/assets/campaign-framework-builder.webm.asset.json";
import campaignPosterAsset from "@/assets/campaign-framework-builder-poster.jpg.asset.json";
import f1ArcadeAsset from "@/assets/f1-arcade-event.png.asset.json";
import milwaukeePanelAsset from "@/assets/milwaukee-hr-ai-panel.png.asset.json";
import gridStabilityAsset from "@/assets/grid-stability.png.asset.json";
import gatorDevicesAsset from "@/assets/gatorguard-responsive-devices.png.asset.json";


type AppExample = {
  category: string;
  audience: string;
  title: string;
  desc: string;
  image: string;
  /** Image already includes device framing, render it without the laptop shell. */
  mockup?: boolean;
  /** Render the framed mockup at half the standard width. */
  mockupSmall?: boolean;
  /** Optional looping video mockup to show in place of the static image. */
  video?: string;
  /** Lightweight poster frame shown before the video loads. */
  videoPoster?: string;
  bullets: string[];
  primaryLabel?: string;
  primaryUrl?: string;
};

const APP_EXAMPLES: AppExample[] = [
  {
    category: "PRODUCT VISUALIZER", audience: "GatorGuard Concrete Coatings",
    title: "GatorGuard Floor Visualizer",
    desc: "A branded experience that helps homeowners picture a new floor using their own space, compare coating styles and save a personalized mockup before requesting a quote. GatorGuard captures qualified leads while shoppers engage before speaking with sales.",
    image: gatorDevicesAsset.url,
    mockup: true,
    bullets: ["Upload-a-Photo Preview", "Compare Coating Styles", "Save & Share Mockups"],
    primaryLabel: "Try the Visualizer", primaryUrl: "https://visualizer.mygatorguard.com",
  },
  {
    category: "CAMPAIGN BUILDER", audience: "Boom Marketing",
    title: "Campaign Framework Builder",
    desc: "An AI-guided builder that scans a prospect's website and returns ad copy, creative mock-ups and a budget plan across Facebook, Instagram, Google and LinkedIn. Prospects leave with a real campaign framework, and Boom leaves with a qualified lead.",
    image: campaignPosterAsset.url,
    video: campaignVideoAsset.url,
    videoPoster: campaignPosterAsset.url,
    bullets: ["Website Scan & AI Copy", "Multi-Platform Ad Mock-Ups", "Budget Recommendations"],
    primaryLabel: "Build a Campaign", primaryUrl: "https://aim.ecommboom.com",
  },
  {
    category: "VALUATION TOOL", audience: "Pharmacy CBS",
    title: "Pharmacy Valuation Tool",
    desc: "A branded calculator that helps pharmacy owners and brokers estimate fair market value from revenue and EBITDA inputs. Captures qualified seller leads while positioning Pharmacy CBS as the go-to advisor for pharmacy transactions.",
    image: pharmacyCbsAsset.url,
    mockup: true,
    bullets: ["Revenue & EBITDA Inputs", "Estimated Valuation Range", "Seller Lead Capture"],
    primaryLabel: "Calculate Value", primaryUrl: "https://assessment.pharmacycbs.com/",
  },
];

const EVENT_EXAMPLES: AppExample[] = [
  {
    category: "EVENT EXPERIENCE", audience: "Pioneer × ServiceNow",
    title: "F1 Arcade Denver Experience",
    desc: "A cinematic invitation for a two-part Denver event: afternoon office hours at Pioneer's Paddock, then full-motion racing and cocktails at F1 Arcade. Registrants pick their sessions, and Pioneer walks away with a qualified guest list.",
    image: f1ArcadeAsset.url,
    bullets: ["Multi-Session Registration", "Venue & Logistics Detail", "Qualified Guest List"],
  },
  {
    category: "EVENT EXPERIENCE", audience: "Pioneer × Milwaukee Event",
    title: "HR + AI Leadership Panel Event",
    desc: "A co-branded registration page for a private dinner and panel with\u00A0\nHR and AI leaders. Built to capture executive RSVPs and support a high-touch event experience.",
    image: milwaukeePanelAsset.url,
    bullets: ["Co-Branded Event Page", "Panel & Agenda Preview", "Executive RSVP Capture"],
  },
  {
    category: "EVENT EXPERIENCE", audience: "Pioneer × RE+",
    title: "The Edge of Grid Stability",
    desc: "A cinematic invitation for an invite-only dinner and panel during RE+ in Las Vegas. It handles RSVP management, a waitlist when seats fill up, automated confirmation and reminder emails, and a self-service backend so the team can update event details, messaging, and attendee status on the fly.",
    image: gridStabilityAsset.url,
    bullets: ["RSVP management + waitlist", "Triggered confirmation emails", "Self-service page updates", "Invite-only executive event"],
  },
];

export function ExampleCard({ app, reverse }: { app: AppExample; reverse: boolean }) {
  return (
    <article className="grid min-w-0 gap-6 sm:gap-8 lg:grid-cols-2 lg:gap-14 lg:items-center">
      {/* Headline */}
      <div className={`order-1 ${reverse ? "lg:order-2 lg:col-start-2" : "lg:order-1"}`}>
        <div className="flex flex-wrap items-center gap-3 mb-3 text-xs">
          <span className="text-primary font-semibold tracking-[0.2em]">{app.category}</span>
          <span className="text-muted-foreground">// {app.audience}</span>
        </div>
        <h3 className="text-2xl sm:text-3xl font-semibold leading-tight">{app.title}</h3>
      </div>

      {/* Media */}
      <div
        className={`order-2 ${
          reverse
            ? "lg:order-1 lg:col-start-1 lg:row-start-1"
            : "lg:order-2 lg:col-start-2 lg:row-start-1"
        } lg:row-span-2`}
      >
        {app.video ? (
          <div className="max-w-[44rem] mx-auto px-2">
            <LaptopVideo
              src={app.video}
              poster={app.videoPoster ?? app.image}
              ariaLabel={`${app.title} video mockup`}
            />
          </div>

        ) : app.mockup ? (
          <div className={app.mockupSmall ? "max-w-[16rem] mx-auto" : "max-w-xl mx-auto"}>
            <img
              src={app.image}
              alt={`${app.title} shown on desktop and mobile`}
              className="w-full h-auto block"
              loading="lazy"
            />
          </div>
        ) : (
          <div className="w-full max-w-2xl mx-auto">
            {/* Laptop screen */}
            <div className="rounded-t-xl bg-[#1a1a1a] p-2 pb-0 shadow-2xl border border-border/60 border-b-0">
              <div className="rounded-md overflow-hidden bg-background aspect-[16/10]">
                <img
                  src={app.image}
                  alt={`${app.title} interface`}
                  className="w-full h-full object-cover object-top block"
                  loading="lazy"
                />
              </div>
            </div>
            {/* Laptop base */}
            <div className="relative h-3 bg-gradient-to-b from-[#2a2a2a] to-[#0f0f0f] rounded-b-[20px]">
              <div className="absolute left-1/2 -translate-x-1/2 top-0 w-1/4 h-[6px] bg-[#0a0a0a] rounded-b-md" />
            </div>
            <div className="mx-auto h-1 w-[85%] bg-black/40 blur-[2px]" />
          </div>
        )}
      </div>

      {/* Body */}
      <div className={`order-3 ${reverse ? "lg:order-2 lg:col-start-2" : "lg:order-1"}`}>
        <p className="mb-5 max-w-prose leading-relaxed text-muted-foreground sm:mb-6">{app.desc}</p>
        <ul className="space-y-2.5 mb-7">
          {app.bullets.map((b) => (
            <li key={b} className="flex items-center gap-3 text-sm">
              <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
        {app.primaryUrl && app.primaryLabel ? (
          <a
            href={app.primaryUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10"
          >
            {app.primaryLabel} <ArrowRight className="w-4 h-4" />
          </a>
        ) : null}
      </div>
    </article>
  );
}

export function ExampleAppsSection() {
  return (
    <section id="example-apps" className="border-t border-border/50 bg-[#080E1E]/95 px-5 py-12 sm:px-6 sm:py-16 md:px-8 md:py-20 lg:px-12">
      <div className="max-w-6xl mx-auto">
        <div className="mx-auto mb-10 max-w-2xl text-center md:mb-16">
          <span className="text-primary font-semibold tracking-[0.2em] text-xs uppercase">Past Projects</span>
          <h2 className="text-3xl sm:text-4xl font-semibold mt-3">Browse our recent&nbsp;work</h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Explore these recent examples of what we can build for your business
          </p>
        </div>

        <div className="space-y-12 md:space-y-20">
          {APP_EXAMPLES.map((app, i) => (
            <ExampleCard key={app.title} app={app} reverse={i % 2 === 1} />
          ))}
        </div>
      </div>
    </section>
  );
}

export { EVENT_EXAMPLES };
